import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Chip, Stack, Typography, CircularProgress, Box, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SupplierProfileHeader from '../../../components/supplier/profile/SupplierProfileHeader';
import SupplierTrustStrip from '../../../components/supplier/profile/SupplierTrustStrip';
import SupplierLocationMap from '../../../components/supplier/profile/SupplierLocationMap';
import SupplierProfileSEO from '../../../components/supplier/profile/SupplierProfileSEO';
import SupplierLocationCard from '../../../components/supplier/profile/SupplierLocationCard';
import { getSupplierProfile } from '../../../components/supplier/requests/supplier-profile-requests';
import type { PublicSupplierProfileVO } from '../../../types/model/valueObject/account/PublicSupplierProfileVO';
import { usePrerenderReady } from '../../../hooks/usePrerenderReady';

const SupplierProfile = () => {
  const { shortId, slug } = useParams<{ shortId: string; slug?: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicSupplierProfileVO | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Known to be gone (not a failed request): only then is the page kept out of search.
  const [missing, setMissing] = useState(false);
  const [activeState, setActiveState] = useState<string | null>(null);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  // Snapshot once the profile fetch settled — including the not-found page.
  usePrerenderReady(!loading);

  const uniqueStates = useMemo(
    () => Array.from(new Set((profile?.locations ?? []).map((l) => l.state).filter(Boolean))).sort() as string[],
    [profile?.locations]
  );

  const filteredLocations = useMemo(
    () => activeState
      ? (profile?.locations ?? []).filter((l) => l.state === activeState)
      : (profile?.locations ?? []),
    [profile?.locations, activeState]
  );

  useEffect(() => {
    if (!shortId) return;

    // A shortId change mid-flight must not let the OLD response populate the
    // page or clear `loading` (which publishes prerenderReady) for the NEW id.
    let stale = false;
    setActiveState(null);
    setLoading(true);
    setNotFound(false);
    setMissing(false);

    getSupplierProfile(shortId, slug || '-')
      .then((res: any) => {
        if (stale) return;
        const data: PublicSupplierProfileVO = res.body;
        // CloudFront answers the API's 404 with the app's index.html (status 200), so a
        // gone supplier arrives as a response with no profile in it.
        if (!data?.shortId) {
          setNotFound(true);
          setMissing(true);
          return;
        }
        setProfile(data);

        // Correct slug if missing or wrong
        if (data.slug && data.slug !== slug) {
          navigate(`/supplier/${data.shortId}/${data.slug}`, { replace: true });
        }
      })
      .catch((err: any) => {
        if (stale) return;
        if (err?.status === 404) {
          setNotFound(true);
          setMissing(true);
        } else {
          console.error('Failed to load supplier profile', err);
          setNotFound(true);
        }
      })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  if (loading) {
    return (
      <>
        <SupplierProfileSEO profile={null} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress color="primary" />
        </Box>
      </>
    );
  }

  if (notFound || !profile) {
    return (
      <>
        <SupplierProfileSEO profile={null} notFound={missing} />
        <Box sx={{ textAlign: 'center', p: '4rem 1rem' }}>
          <Typography sx={{ fontFamily: 'Satoshi-Variable, sans-serif', fontWeight: 700, fontSize: '1.5rem', mb: 2 }}>
            Supplier Not Found
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            The supplier profile you're looking for doesn't exist or has been removed.
          </Typography>
          <Link to="/yards" className="ss-button-primary" style={{ display: 'inline-block', marginTop: '1rem' }}>
            Return to Search
          </Link>
        </Box>
      </>
    );
  }

  const { locations } = profile;

  return (
    <>
      <SupplierProfileSEO profile={profile} />
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3 }, pb: 6 }}>
        <SupplierProfileHeader profile={profile} />

        <SupplierTrustStrip profile={profile} />

        {/* Map banner */}
        {locations.length > 0 && (
          <Box sx={{ mb: 4 }}>
            {/* Mobile-only toggle */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setMobileMapOpen((prev) => !prev)}
              aria-expanded={mobileMapOpen}
              aria-controls="supplier-map-panel"
              endIcon={<ExpandMoreIcon sx={{ transform: mobileMapOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />}
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, mb: mobileMapOpen ? 1.5 : 0, textTransform: 'none' }}
            >
              {mobileMapOpen ? 'Hide map' : 'Show map'}
            </Button>

            <Box
              id="supplier-map-panel"
              sx={{
                // Desktop: always visible at full height
                // Mobile: height animates based on toggle
                height: { xs: mobileMapOpen ? 380 : 0, sm: 380 },
                overflow: 'hidden',
                borderRadius: 3,
                border: { xs: mobileMapOpen ? '1px solid #E9E9FB' : 'none', sm: '1px solid #E9E9FB' },
                transition: 'height 0.3s ease',
              }}
            >
              <SupplierLocationMap locations={locations} />
            </Box>
          </Box>
        )}

        {/* Locations heading + state filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Satoshi-Variable, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              mr: 'auto',
            }}
          >
            All locations ({filteredLocations.length})
          </Typography>

          {uniqueStates.length > 1 && (
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              <Chip
                label="All"
                size="small"
                onClick={() => setActiveState(null)}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  bgcolor: activeState === null ? 'primary.main' : undefined,
                  color: activeState === null ? '#fff' : undefined,
                  borderColor: activeState === null ? 'primary.main' : '#E9E9FB',
                  fontWeight: activeState === null ? 600 : 400,
                  '&:hover': { bgcolor: activeState === null ? 'primary.dark' : 'rgba(63,81,181,0.08)' },
                }}
                variant={activeState === null ? 'filled' : 'outlined'}
              />
              {uniqueStates.map((state) => (
                <Chip
                  key={state}
                  label={state}
                  size="small"
                  onClick={() => setActiveState(state === activeState ? null : state)}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.82rem',
                    bgcolor: activeState === state ? 'primary.main' : undefined,
                    color: activeState === state ? '#fff' : undefined,
                    borderColor: activeState === state ? 'primary.main' : '#E9E9FB',
                    fontWeight: activeState === state ? 600 : 400,
                    '&:hover': { bgcolor: activeState === state ? 'primary.dark' : 'rgba(63,81,181,0.08)' },
                  }}
                  variant={activeState === state ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          )}
        </Box>

        {filteredLocations.length === 0 ? (
          <Typography variant="body1" color="textSecondary">
            This supplier has no active locations at this time.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            {filteredLocations.map((loc) => (
              <SupplierLocationCard
                key={loc.locationId}
                location={loc}
                variant="grid"
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default SupplierProfile;
